import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Lock, BookOpen, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

function itemHash(text) {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36);
}

// Converts "- [ ] task" / "- [x] task" lines into interactive action items.
function mdToHtml(md) {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  let actionIdx = 0;
  for (const raw of lines) {
    const line = raw.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");
    const task = raw.match(/^\s*[-*]\s\[[ xX]?\]\s+(.+)$/);
    if (/^#\s/.test(line)) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h1>${line.slice(2)}</h1>`); }
    else if (/^##\s/.test(line)) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h2>${line.slice(3)}</h2>`); }
    else if (/^###\s/.test(line)) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h3>${line.slice(4)}</h3>`); }
    else if (task) {
      if (!inList) { out.push("<ul>"); inList = true; }
      const text = task[1].replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");
      const key = `a${actionIdx}-${itemHash(task[1])}`;
      actionIdx += 1;
      out.push(`<li class="gf-action" data-key="${key}" data-status="pending" role="button" tabindex="0"><span class="gf-action-box" aria-hidden="true">✓</span><span>${text}</span></li>`);
    }
    else if (/^\s*[-*]\s/.test(line)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${line.replace(/^\s*[-*]\s/, "")}</li>`);
    } else if (line.trim() === "") {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push("");
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<p>${line}</p>`);
    }
  }
  if (inList) out.push("</ul>");
  return out.join("\n");
}

const fmtMoney = (n) => `$${Number(n).toFixed(2)}`;

export default function TemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [progress, setProgress] = useState({});
  const contentRef = useRef(null);

  const load = () => api.get(`/templates/${id}`).then((r) => setTpl(r.data)).catch(() => navigate("/templates"));

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (cancelled) setStatusMsg("Payment cancelled.");
  }, [cancelled]);

  useEffect(() => {
    if (!sessionId) return;
    let n = 0;
    const poll = async () => {
      n += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatusMsg("Purchase complete — enjoy the guide!");
          toast.success("Template unlocked");
          await load();
          return;
        }
        if (data.status === "expired") { setStatusMsg("Session expired."); return; }
        if (n < 10) setTimeout(poll, 2000);
        else setStatusMsg("Still processing — refresh in a minute.");
      } catch {
        setStatusMsg("Could not verify payment yet.");
      }
    };
    setStatusMsg("Verifying payment…");
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/template-checkout", {
        template_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  const safeHtml = useMemo(
    () => DOMPurify.sanitize(mdToHtml(tpl?.content || ""), {
      ALLOWED_TAGS: ["p", "br", "ul", "ol", "li", "span", "strong", "em", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "code", "pre"],
      ALLOWED_ATTR: ["class", "role", "tabindex", "aria-hidden"],
    }),
    [tpl?.content],
  );

  // Load per-user checklist progress once the template is owned.
  useEffect(() => {
    if (!tpl?.owned) return;
    api.get(`/templates/${id}/progress`)
      .then(({ data }) => setProgress(data.items || {}))
      .catch(() => {});
    // eslint-disable-next-line
  }, [tpl?.owned, id]);

  // Paint statuses onto the rendered action items.
  useEffect(() => {
    if (!contentRef.current) return;
    contentRef.current.querySelectorAll("li.gf-action").forEach((el) => {
      el.dataset.status = progress[el.dataset.key] || "pending";
    });
  }, [safeHtml, progress, tpl?.owned]);

  const NEXT_STATUS = { pending: "completed", completed: "blocked", blocked: "pending" };

  const onContentClick = async (e) => {
    const li = e.target.closest("li.gf-action");
    if (!li || !tpl?.owned) return;
    const key = li.dataset.key;
    const next = NEXT_STATUS[progress[key] || "pending"];
    setProgress((p) => ({ ...p, [key]: next }));
    try {
      await api.put(`/templates/${id}/progress`, { item_key: key, status: next });
    } catch {
      toast.error("Could not save progress");
    }
  };

  const actionTotals = useMemo(() => {
    const total = (tpl?.content?.match(/^\s*[-*]\s\[[ xX]?\]\s+/gm) || []).length;
    const completed = Object.values(progress).filter((s) => s === "completed").length;
    const blocked = Object.values(progress).filter((s) => s === "blocked").length;
    return { total, completed, blocked };
  }, [tpl?.content, progress]);

  const previewHtml = useMemo(
    () => DOMPurify.sanitize(mdToHtml(tpl?.preview || ""), {
      ALLOWED_TAGS: ["p", "br", "ul", "ol", "li", "strong", "em", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "code", "pre"],
      ALLOWED_ATTR: [],
    }),
    [tpl?.preview],
  );

  if (!tpl) return <div className="gf-overline">Loading…</div>;

  return (
    <div className="max-w-5xl">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900 uppercase tracking-wide" data-testid="tpl-back">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="mt-6 border border-zinc-200 bg-white p-6 md:p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="gf-overline">{tpl.category}</div>
          {!tpl.owned && (
            <div className="font-display font-black text-3xl tracking-tight">{fmtMoney(tpl.price_usd)}</div>
          )}
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3" data-testid="tpl-title">
          {tpl.title}
        </h1>
        <p className="text-zinc-600 text-sm mt-3 leading-relaxed">{tpl.summary}</p>

        {statusMsg && (
          <div data-testid="tpl-status" className="mt-4 border border-blue-300 bg-blue-50 text-blue-900 px-4 py-3 text-sm">
            {statusMsg}
          </div>
        )}

        {!tpl.owned && (
          <Button
            onClick={buy}
            disabled={busy}
            data-testid="tpl-buy"
            className="gf-sharp mt-5 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide"
          >
            {busy ? "Redirecting…" : `Buy template — ${fmtMoney(tpl.price_usd)}`}
          </Button>
        )}
      </div>

      <div className="mt-6 border border-zinc-200 bg-white p-6 md:p-10">
        {tpl.owned ? (
          <div>
            {actionTotals.total > 0 && (
              <div className="mb-6 border border-zinc-200 bg-zinc-50 px-4 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="tpl-progress-legend">
                <div className="flex items-center gap-4 text-[11px] font-mono uppercase tracking-widest text-zinc-600">
                  <span className="flex items-center gap-1.5"><span className="gf-legend-box" style={{ background: "#059669" }}>✓</span> Completed</span>
                  <span className="flex items-center gap-1.5"><span className="gf-legend-box" style={{ background: "#d97706" }}>✓</span> Pending</span>
                  <span className="flex items-center gap-1.5"><span className="gf-legend-box" style={{ background: "#dc2626" }}>✓</span> Unable</span>
                </div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500" data-testid="tpl-progress-count">
                  {actionTotals.completed}/{actionTotals.total} done
                  {actionTotals.blocked > 0 && ` · ${actionTotals.blocked} unable`}
                </div>
              </div>
            )}
            <div
              ref={contentRef}
              onClick={onContentClick}
              data-testid="tpl-content"
              className="prose-grant"
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          </div>
        ) : (
          <TemplatePreviewBlock tpl={tpl} previewHtml={previewHtml} busy={busy} buy={buy} />
        )}
      </div>
    </div>
  );
}

function TocHeader({ toc }) {
  const free = toc.filter((h) => h.free).length;
  const locked = toc.length - free;
  return (
    <div className="flex items-baseline justify-between gap-2">
      <div className="gf-overline">What's inside</div>
      {locked > 0 && (
        <div data-testid="tpl-toc-counter" className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          <span className="text-emerald-700 font-bold">{free} free</span>
          <span className="mx-1.5 text-zinc-300">·</span>
          <span className="text-amber-700 font-bold">{locked} locked</span>
        </div>
      )}
    </div>
  );
}

function TemplatePreviewBlock({ tpl, previewHtml, busy, buy }) {
  const wordCount = tpl.content_word_count;
  return (
    <div data-testid="tpl-preview">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="gf-overline flex items-center gap-2">
          <BookOpen size={14} /> Live preview
        </div>
        {wordCount > 0 && (
          <div className="font-mono text-xs uppercase tracking-wider text-zinc-500">
            {wordCount.toLocaleString()} words total
          </div>
        )}
      </div>
      {tpl.toc && tpl.toc.length > 0 && (
        <div className="mt-4 border border-zinc-200 bg-zinc-50 p-4" data-testid="tpl-toc">
          <TocHeader toc={tpl.toc} />
          <ul className="mt-2 space-y-1 text-sm text-zinc-700">
            {tpl.toc.map((h, i) => (
              <li
                key={i}
                data-testid={`tpl-toc-row-${i}`}
                className={`group ${h.level === 1 ? "" : h.level === 2 ? "ml-3" : "ml-6"}`}
              >
                <div
                  className={`flex items-center gap-2 ${
                    h.level === 1 ? "font-bold tracking-tight"
                    : h.level === 2 ? "text-zinc-800"
                    : "text-zinc-600 text-xs"
                  } ${h.free ? "" : "opacity-70 group-hover:opacity-100 transition-opacity"}`}
                >
                  {h.level > 1 && <span className="text-zinc-400">{h.level === 2 ? "→" : "·"}</span>}
                  <span className={h.free ? "" : "italic"}>{h.text}</span>
                  {!h.free && (
                    <LockKey
                      size={h.level === 3 ? 11 : 13}
                      className="text-amber-600 shrink-0 group-hover:text-amber-700 transition-colors"
                      aria-label="Locked section"
                    />
                  )}
                </div>
                {!h.free && h.teaser && (
                  <div
                    data-testid={`tpl-toc-teaser-${i}`}
                    className="overflow-hidden max-h-0 group-hover:max-h-20 transition-all duration-300 ease-out ml-5"
                  >
                    <p className="mt-1 text-xs text-zinc-500 italic leading-relaxed">
                      {h.teaser}
                    </p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className="prose-grant mt-6 relative"
        style={{
          // Soft fade-to-white on the bottom 80px so the cut-off looks intentional.
          maskImage: "linear-gradient(to bottom, black 0%, black 75%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 75%, transparent 100%)",
        }}
        dangerouslySetInnerHTML={{ __html: previewHtml }}
      />
      <div className="mt-2 border-t border-zinc-200 pt-6 flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-zinc-600 flex items-center gap-2">
          <Lock size={16} /> Buy to unlock the full guide.
        </div>
        <Button
          onClick={buy}
          disabled={busy}
          data-testid="tpl-buy-bottom"
          className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide"
        >
          {busy ? "Redirecting…" : `Unlock — ${fmtMoney(tpl.price_usd)}`}
        </Button>
      </div>
    </div>
  );
}
